"use client"

import axios from "axios"
import moment from "moment"
import React, { useEffect, useRef, useState } from "react"
import { NotificationManager } from "react-notifications"
import { useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import contact from "../../assets/svgs/account.svg"
import DownArrow from "../../assets/svgs/down-arrow.svg"
import Plus from "../../assets/svgs/Plus.svg"
import sortAsce from "../../assets/svgs/sort-ascending.svg"
import sortDesc from "../../assets/svgs/sort-descending.svg"
import swap from "../../assets/svgs/swap-vertical.svg"
import UpArrow from "../../assets/svgs/up-arrow.svg"
import ActionsMenu from "../../components/ActionsMenu"
import BarData from "../../components/Charts/BarData"
import CircleChart from "../../components/Charts/CircleChart"
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal"
import Loader from "../../components/Loader"
import ContactPagination from "../../components/Pagination/ContactPagination"
import useAuth from "../../hooks/useAuth"
import BaseLayout from "../../layouts/BaseLayout"
import { BASE_URL } from "../../utils/Element"
import { initialLeadFilterData } from "../../utils/initialData"
import AddLeadModal from "./AddLeadModal"
import ConvertPipelineModal from "./components/ConvertPipelineModal"
import LeadDataContainer from "./components/LeadDataContainer"
import LeadDataContainer2 from "./components/LeadDataContainer2"
import LeadsFilter from "./components/LeadsFilter"
import EditLeadModal from "./EditLeadModal"

const Leads = () => {
  const [config] = useAuth()
  const navigate = useNavigate()

  // ─── Search + Debounce states ────────────────────────────────
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef(null)
  const [selectedLeadType, setSelectedLeadType] = useState(null);
  const [loading, setLoading] = useState(true)
  const [leadsData, setLeadsData] = useState([])
  const [selectedId, setSelectedId] = useState("")
  const [bdsOptions, setBdsOptions] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemPerPage, setItemPerPage] = useState(50)
  const [sortBy, setSortBy] = useState("id")
  const [leadDashboard, setLeadDashboard] = useState()
  const [sortDirection, setSortDirection] = useState("desc")
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [disableDelete, setDisableDelete] = useState(false)
  const [expandedRows, setExpandedRows] = useState({})
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [rowStatuses, setRowStatuses] = useState({})
  const [activeTab, setActiveTab] = useState(0)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState({
    this_week: false,
    lead_not_contacted: false,
    total_lead_ytd: false,
    new_lead_quarter: false,
  })
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed)
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  })
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : initialLeadFilterData
  })
  const [selectedUser, setSelectedUser] = useState(null)
  const [originalUserData, setOriginalUserData] = useState(null)
  const [isSwitchingUser, setIsSwitchingUser] = useState(false)

  // ─── Debounce logic ───────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value
    const trimmed = value.startsWith(" ") ? value.trimStart() : value
    setSearch(trimmed)
  }

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      // Only trigger if empty or ≥ 3 characters
      if (search.length === 0 || search.length >= 3) {
        setDebouncedSearch(search.trim())
      }
    }, 450)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  const statusOptions = [
    { value: 2, label: "Cold" },
    { value: 3, label: "Warm" },
    { value: 1, label: "Hot" },
  ]

  const colorMap = { 1: "#ff0000", 3: "#ffb952", 2: "blue" }

  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}`

    // Use debounced search value
    if (debouncedSearch) {
      params += `&search=${encodeURIComponent(debouncedSearch)}`
    }

    const {
      bds,
      broker,
      lead_source,
      created_at,
      created_end_date,
      created_start_date,
      contact,
      lead_type,
      lead_type_ytd,
      link_id,
      property,
      buyer_id,
      last_contact,
      lastContactSentDate,
      lastContactSentEdate,
      lastContactSentSdate,
      within_last_day,
      longer_than_day,
      date_category,
      this_week,
      lead_not_contacted,
      total_lead_ytd,
      new_lead_quarter,
    } = filters

    if (bds) params += `&bds=${encodeURIComponent(bds)}`
    if (broker) params += `&broker=${encodeURIComponent(broker)}`
    if (lead_source?.length > 0) params += `&lead_source=${encodeURIComponent(lead_source)}`
    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`
    if (contact?.id) params += `&contact=${encodeURIComponent(contact.id)}`
    if (lead_type) params += `&lead_type=${encodeURIComponent(lead_type === "acquisition" ? 1 : 0)}`
    if (lead_type_ytd) params += `&lead_type_ytd=${encodeURIComponent(lead_type_ytd === "acquisition" ? 1 : 0)}`
    if (last_contact && (lastContactSentDate || (lastContactSentSdate && lastContactSentEdate))) {
      params += `&last_contact=${encodeURIComponent(last_contact)}`
    }
    if (lastContactSentDate) params += `&last_update_date=${encodeURIComponent(lastContactSentDate)}`
    if (lastContactSentSdate) params += `&last_update_start_date=${encodeURIComponent(lastContactSentSdate)}`
    if (lastContactSentEdate) params += `&last_update_end_date=${encodeURIComponent(lastContactSentEdate)}`
    if (within_last_day) params += `&within_last_day=${encodeURIComponent(within_last_day)}`
    if (longer_than_day) params += `&longer_than_day=${encodeURIComponent(longer_than_day)}`
    if (date_category !== undefined) params += `&date_category=${encodeURIComponent(date_category)}`
    if (link_id) params += `&link_id=${encodeURIComponent(link_id)}`
    if (property?.id) params += `&property_id=${encodeURIComponent(property.id)}`
    if (buyer_id) params += `&buyer_id=${encodeURIComponent(buyer_id)}`

    // Filter status flags
    if (filterStatus.this_week) params += `&this_week=true`
    if (filterStatus.lead_not_contacted) params += `&lead_not_contacted=true`
    if (filterStatus.total_lead_ytd) params += `&total_lead_ytd=true`
    if (filterStatus.new_lead_quarter) params += `&new_lead_quarter=true`

    if (sortDirection && sortBy !== "id") params += `&sort_direction=${sortDirection}`
    if (sortBy && sortBy !== "id") params += `&sort_by=${sortBy}`
    if (selectedUser?.value) params += `&show_user_id=${encodeURIComponent(selectedUser.value)}`

    return params
  }

  const fetchLeads = (filters = filterData) => {
    const queryParams = buildQueryParams(filters)
    setLoading(true)

    axios
      .get(`${BASE_URL}/list-leads?active_tab=${activeTab}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.leadsList
        setLeadsData(value?.data || [])
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        })
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  // ─── Main effect triggers ─────────────────────────────────────
  useEffect(() => {
    fetchLeads(filterData)
  }, [
    sortDirection,
    sortBy,
    filterStatus,
    activeTab,
    currentPage,
    itemPerPage,
    debouncedSearch,      // ← key change
    selectedUser,
  ])

  // Reset page when significant filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, activeTab, filterStatus, selectedUser])

  const onSuccess = (filters) => {
    fetchLeads(filters || filterData)
  }

  // ─── Rest of your handlers (mostly unchanged) ─────────────────

  const handleNext = () => {
    if (currentPage < paginationData.totalPages) setCurrentPage(currentPage + 1)
  }

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const handleStatusChange = (value, leadId) => {
    setRowStatuses((prev) => ({ ...prev, [leadId]: value }))

    const selectedOption = statusOptions.find((opt) => opt.value === value)

    axios
      .post(
        `${BASE_URL}/lead-status-change/${leadId}`,
        { lead_status: value, type: selectedOption?.label },
        config
      )
      .then((res) => {
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const handleDelete = () => {
    setDisableDelete(true)
    axios
      .delete(`${BASE_URL}/delete-lead/${selectedId}`, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowDeleteModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
      .finally(() => setDisableDelete(false))
  }

  const handleConfirm = () => {
    axios
      .post(`${BASE_URL}/markAs-Proposal/${selectedId}`, { mark_as_proposal: true }, config)
      .then((res) => {
        onSuccess()
        setShowConfirmModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const fetchDashboardData = () => {
    let url = `${BASE_URL}/dashboard-lead?active_tab=${activeTab}`
    if (selectedUser?.value) url += `&show_user_id=${selectedUser.value}`

    axios
      .get(url, config)
      .then((res) => setLeadDashboard(res?.data?.lead_dashboard))
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const fetchUsers = () => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=users`, config)
      .then((res) => {
        const options = res?.data?.data?.map((el) => ({
          value: el.id,
          label: `${el.first_name} ${el.last_name}`,
          role: el.role_id,
        }))
        setBdsOptions(options)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const leadTypeValue =
  selectedLeadType === 0 ? "Disposition" : "Acquisition";

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    if ([0, 2].includes(activeTab)) fetchDashboardData()
  }, [selectedUser, activeTab])

  const handleBoxClick = (statusKey) => {
    let targetTab = activeTab === 0 ? 1 : 3

    setActiveTab(targetTab)
    setCurrentPage(1)
    setFilterStatus({
      this_week: false,
      lead_not_contacted: false,
      total_lead_ytd: false,
      new_lead_quarter: false,
      [statusKey]: true,
    })
  }

  const data = leadDashboard?.lead_source_leads || [];

  const BarGraphData = [
    { name: "Buyer", count: leadDashboard?.lead_type_ytd_buyer || leadDashboard?.lead_type_buyer || 0 },
    { name: "Seller", count: leadDashboard?.lead_type_ytd_seller || leadDashboard?.lead_type_seller || 0 },
  ];

  const handleUserSwitch = (option) => {
    if (!option) {
      setSelectedUser(null)
      setIsSwitchingUser(false)
      onSuccess()
      return
    }

    if (!isSwitchingUser) {
      setOriginalUserData(leadsData)
      setIsSwitchingUser(true)
    }

    setSelectedUser(option)
    onSuccess()
  }

  const getDisplayText = (el) => {
    if (el?.lead_type === 0 && el.portfolio !== 1) {
      if (!el?.link?.property_name || !el?.link_address_name) {
        return ""
      }
      return (
        <>
          <span>{el.link.property_name}</span>
          <br />
          <span>{el.link_address_name.street}</span>
          <br />
          <span>{el.link_address_name.city}, {el.link_address_name.state}</span>
        </>
      )
    }
    if (el?.lead_type === 1 && el.portfolio !== 1) {
      return el?.link?.property_type?.[0]?.type || ""
    }
    return el?.link?.name || ""
  }

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  // ─── JSX ──────────────────────────────────────────────────────
  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">Leads</p>
        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center">
            <LeadsFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="lead"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts">
              <input
                type="text"
                className="body-S"
                placeholder="Search name, contact count, duration, touches..."
                value={search}
                onChange={handleSearchChange}   // ← updated handler
                style={{ border: "1px solid #D8D8D8", marginTop: "0px" }}
              />
            </div>

            <button
              onClick={() => setShowAddModal(true)}
              className="add-contact-button green-bg-H light-L body-S"
            >
              <img className="mr-2" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="task-tabs mx-1 mt-2 flex justify-between body-L dark-M mb-4">
        <div className="flex gap-4 flex-between">
          {["My Dashboard", "My Leads", "Team Dashboard", "Team Leads"].map((tab, index) => (
            <p
              key={index}
              role="button"
              className={`${activeTab === index ? "head-4 dark-H active" : ""} py-1`}
              onClick={() => {
                setActiveTab(index)
                setCurrentPage(1)
                // Reset filterStatus when switching tabs
                setFilterStatus({
                  this_week: false,
                  lead_not_contacted: false,
                  total_lead_ytd: false,
                  new_lead_quarter: false,
                })
              }}
            >
              {tab}
            </p>
          ))}
        </div>
      </div>

      {(activeTab === 1 || activeTab === 3) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : leadsData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Leads Available</p>
            ) : (
              <table className="team-leads-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">

                    <th>
                      <p>Details</p>
                    </th>

                    <th>
                      <div className="table-header">
                        Lead Date
                        <img
                          role="button"
                          src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("created_at")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Contact Name
                        <img
                          role="button"
                          src={sortBy !== "contact_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("contact_id")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Lead Type
                        <img
                          role="button"
                          src={sortBy !== "type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("type")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    {/* <th>
                      <div className="table-header">
                        Portfolio
                        <img
                          role="button"
                          src={sortBy !== "portfolio" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("portfolio")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th> */}
                    {/* 
                    <th>
                      <div className="table-header">
                        Status
                        <img
                          role="button"
                          src={sortBy !== "lead_status" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("lead_status")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th> */}

                    <th>
                      <div className="table-header">
                        Property /
                        <br />
                        Acquisition Criteria
                        <img
                          role="button"
                          src={sortBy !== "link_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("link_id")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Lead Source
                        <img
                          role="button"
                          src={sortBy !== "lead_source_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("lead_source_id")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        Last Contacted
                        <img
                          role="button"
                          src={sortBy !== "last_update_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("last_update_date")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">Actions</div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {leadsData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr key={idx} className="dark-H body-N">
                        <td>
                          <img
                            role="button"
                            src={expandedRows[el.id] ? UpArrow : DownArrow}
                            alt="icon"
                            className="sidebar-icons"
                            onClick={() => toggleRowDetails(el.id)}
                          />
                        </td>

                        <td className="dark-M">{moment(el?.created_at).tz("America/New_York").format("MM/DD/YY")}</td>
                        <td className="flex items-center head-5 green-H gap-2 capitalize">
                          <Link
                            to={`/contact/${el?.contact?.id}`}
                            onClick={(e) => {
                              e.preventDefault()
                              if (el?.contact?.id) {
                                window.open(`/contact/${el?.contact?.id}`, "_blank", "noopener,noreferrer")
                              }
                            }}
                            rel="noopener noreferrer"
                            className="flex items-center"
                          >
                            {(el?.contact?.first_name || el?.contact?.last_name) && (
                              <img src={contact || "/placeholder.svg"} alt="contact" className="sidebar-icons" />
                            )}
                            {el?.contact?.first_name} {el?.contact?.last_name}
                          </Link>
                        </td>

                        <td>{el?.lead_type === 0 ? "Disposition" : "Acquisition"}</td>
                        {/* <td className="dark-M items-center">{el?.portfolio ? "Yes" :  "No"}</td> */}

                        {/* <td>
                        <Select
                          options={statusOptions}
                          value={
                            statusOptions.find((option) => option.value === (rowStatuses[el.id] || el.lead_status)) ||
                            null
                          }
                          onChange={(option) => handleStatusChange(option.value, el.id)}
                          placeholder="Status"
                          className="body-N lead-width"
                          styles={{
                            singleValue: (base, { data }) => ({ ...base, color: colorMap[data.value] }),
                            option: (base, { isFocused }) => ({
                              ...base,
                              backgroundColor: isFocused ? "#f0f0f0" : "white",
                              color: "black",
                            }),
                          }}
                        />
                      </td> */}

                        <td className="head-5 capitalize">
                          <Link
                            to={
                              el?.portfolio === 0
                                ? `/property/${el?.link?.id}`
                                : el?.portfolio === 1 && el?.link?.owner_info_type === "contact"
                                  ? `/contact/${el?.link?.owner?.id}`
                                  : el?.portfolio === 1 && el?.link?.owner_info_type === "company"
                                    ? `/company/${el?.link?.owner?.id}`
                                    : el?.lead_type === 0
                                      ? `/property/${el?.link?.id}`
                                      : el?.lead_type === 1
                                        ? `/buyer/${el?.link?.id}`
                                        : "#"
                            }
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.preventDefault();

                              if (el?.portfolio === 0) {
                                navigate(`/property/${el?.link?.id}`);
                              } else if (el?.portfolio === 1 && el?.link?.owner_info_type === "contact") {
                                navigate(`/contact/${el?.link?.owner?.id}`);
                              } else if (el?.portfolio === 1 && el?.link?.owner_info_type === "company") {
                                navigate(`/company/${el?.link?.owner?.id}`);
                              } else if (el?.lead_type === 0) {
                                navigate(`/property/${el?.link?.id}`);
                              } else if (el?.lead_type === 1) {
                                navigate(`/buyer/${el?.link?.id}`);
                              }
                            }}
                          >
                            {getDisplayText(el)}
                          </Link>
                        </td>

                        <td className="dark-M">{el?.lead_source?.name}</td>

                        <td className="dark-M">
                          {el?.contact?.get_latest_activity?.date_connected
                            ? moment(el?.contact?.get_latest_activity?.date_connected)
                              .tz("America/New_York")
                              .format("MM/DD/YY")
                            : ""}{" "}
                          <span className="dark-M body-S">
                            {el?.contact?.get_latest_activity?.date_connected
                              ? ` (${moment(el?.contact?.get_latest_activity?.date_connected).fromNow()})`
                              : ""}
                          </span>
                        </td>

                        <td className="flex gap-3 items-center w-full justify-center">
                          <ActionsMenu
                            from="lead"
                            handleEdit={() => {
                              setShowEditModal(true)
                              setSelectedId(el?.id)
                            }}
                            handleDelete={() => {
                              setSelectedId(el?.id)
                              setShowDeleteModal(true)
                            }}
                            showOtherOption={el.type === 0 ? true : false}
                            otherOptionTitle={"Convert to Pipeline"}
                            handleOtherOption={() => {
                              setShowConfirmModal(true)
                              setSelectedId(el?.id)
                              setSelectedLeadType(el?.lead_type)
                            }}
                          />
                        </td>
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="flex items-center gap-6 mb-7">

                                <div>
                                  <label className="dark-H head-4 block">BDS</label>
                                  <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                    <span>
                                      <Link
                                        to={`/user/${el?.bds?.id}`}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          if (el?.bds?.id) {
                                            navigate(`/user/${el?.bds?.id}`)
                                          }
                                        }}
                                        rel="noopener noreferrer"
                                      >
                                        {el?.bds?.first_name} {el?.bds?.last_name}
                                      </Link>
                                    </span>
                                  </p>
                                </div>

                                <div>
                                  <label className="dark-H head-4 block">Broker</label>
                                  <p className="body-N dark-H pt-2 min-h-[1.5rem] space-y-1">
                                    {el?.broker_details?.length > 0 ? (
                                      el.broker_details.map((broker) => (
                                        <span key={broker.id} className="block">
                                          <Link
                                            to={`/user/${broker.id}`}
                                            onClick={(e) => {
                                              e.preventDefault()
                                              navigate(`/user/${broker.id}`)
                                            }}
                                            rel="noopener noreferrer"
                                            className="text-gray-800 hover:text-green-700"
                                          >
                                            {broker.first_name} {broker.last_name}
                                          </Link>
                                        </span>
                                      ))
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </p>
                                </div>


                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {leadsData?.length > 0 && (
            <ContactPagination
              className="py-4"
              activeTab={activeTab}
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              onSuccess={onSuccess}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value)
                setCurrentPage(1)
              }}
            />
          )}
        </div>
      )}

      {(activeTab === 0 || activeTab === 2) && (
        <>
          <div className="flex gap-3">
            <div className="w-[65%]">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <div className="h-full">
                  <LeadDataContainer
                    text="NEW LEADS PAST 7 DAYS"
                    number={leadDashboard?.lead_week_count}
                    className="min-h-[8rem] w-full"
                    onClick={() => handleBoxClick("this_week")}
                  />
                </div>
                <div className="h-full">
                  <LeadDataContainer
                    text="NEW LEADS THIS QUARTER"
                    number={leadDashboard?.lead_quarter_count}
                    className="min-h-[8rem] w-full"
                    onClick={() => handleBoxClick("new_lead_quarter")}
                  />
                </div>
                <div className="h-full">
                  <LeadDataContainer
                    text="TOTAL LEADS YTD"
                    number={leadDashboard?.lead_count}
                    className="min-h-[8rem] w-full"
                    onClick={() => handleBoxClick("total_lead_ytd")}
                  />
                </div>
                <div className="h-full">
                  <LeadDataContainer
                    text="WARM & HOT LEADS NOT CONTACTED IN 14+ DAYS"
                    number={leadDashboard?.lead_status_count}
                    className="min-h-[8rem] w-full"
                    onClick={() => handleBoxClick("lead_not_contacted")}
                  />
                </div>
              </div>

              <div className="flex items-center gap-5">
                <CircleChart
                  Title="Lead Source"
                  TotalTitle="Total Leads"
                  Total={leadDashboard?.lead_source_count}
                  data={data}
                />
                <BarData
                  Title="Type of Lead"
                  className="w-[50%]"
                  BarGraphData={BarGraphData}
                  onBarClick={(leadType) => {
                    setActiveTab(3)
                    setCurrentPage(1)
                    const newFilterData = {
                      ...filterData,
                      lead_type_ytd: leadType,
                    }
                    setFilterData(newFilterData)
                    localStorage.setItem("filterData", JSON.stringify(newFilterData))
                    // Use setTimeout to ensure state is updated before fetching
                    setTimeout(() => {
                      fetchLeads(newFilterData)
                    }, 0)
                  }}
                />
              </div>
            </div>

            <div className="w-[35%]">
              <LeadDataContainer2
                text="HOT LEADS"
                data={leadDashboard?.lead_status_hot ?? []}
                columns={[
                  { key: "contact_name", label: "Contact Name" },
                  {
                    key: "link_name",
                    label: (
                      <>
                        Property/
                        <br />
                        Acquisition Criteria
                      </>
                    ),
                  },
                ]}
              />

              <LeadDataContainer2
                text="WARM LEADS"
                data={leadDashboard?.lead_status_warm ?? []}
                columns={[
                  { key: "contact_name", label: "Contact Name" },
                  {
                    key: "link_name",
                    label: (
                      <>
                        Property/
                        <br />
                        Acquisition Criteria
                      </>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </>
      )}

      <AddLeadModal from="Lead" showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />

      <EditLeadModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("")
          onSuccess()
        }}
        from="Lead"
      />

      <ConvertPipelineModal
        showModal={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        from="lead"
        id={selectedId}
        leadType={leadTypeValue}  
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />
    </BaseLayout>
  )
}

export default Leads
